import React, { useEffect, useMemo, useState } from 'react';
import Axios from 'axios';
import {
  Modal,
  Tabs,
  Collapse,
  Descriptions,
  Tag,
  List,
  Button,
  Space,
  Spin,
  Empty,
  message,
} from 'antd';
import {
  EditOutlined,
  DownloadOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { getSubTypeLabel, getSubTypesForSurvey } from '../../Config/subType';

/**
 * QSetDetailModal — 設問セット詳細モーダル
 * ------------------------------------------------------------------
 * 機能企画書 F-04, F-05, F-08 に対応する詳細表示モーダル。
 *
 * 表示内容:
 *   - セット基本情報 (set_name, set_description, type_360 等)
 *   - サーベイコードに応じた sub_type 別タブ
 *     - 40010 / 60020: 本人用·協力者用 (2タブ)
 *     - 20110 / 20210: 事前·直後·事後 (3タブ)
 *   - 各タブで質問をカテゴリ単位に Collapse 표시
 *   - 使用中シーズン一覧 (F-08)
 *   - 編集ボタン → EditQSetPage 遷移 (F-02 連携、非カスタマイズなら非活性)
 *   - CSV ダウンロードボタン (F-05)
 */
const QSetDetailModal = ({
  visible,
  onClose,
  questionSetId,
  allSets = [],
  onEdit,
  onExport,
}) => {
  const [loading, setLoading] = useState(false);
  // sub_type ごとの詳細データ {'4': {...}, '5': {...}} など
  const [detailsBySubType, setDetailsBySubType] = useState({});
  const [activeTab, setActiveTab] = useState(null);
  const [seasonModalSeason, setSeasonModalSeason] = useState(null);

  // クリックされたセット
  const clickedSet = useMemo(() => {
    if (!questionSetId) return null;
    return allSets.find((s) => s._id === questionSetId);
  }, [questionSetId, allSets]);

  // サーベイコードに応じて表示すべき sub_type 一覧
  const expectedSubTypes = useMemo(() => {
    if (!clickedSet) return [];
    return getSubTypesForSurvey(clickedSet.survey_code);
  }, [clickedSet]);

  // -------------------------------------------------------------------
  //  クリックされたセット + 同 set_number の兄弟セットを並列取得
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!visible || !clickedSet) return;

    // set_name から S\d+ を抽出
    const match = String(clickedSet.set_name || '').match(/S\d+/);
    const setNumber = match ? match[0] : null;

    // 兄弟検索: survey_code + type_360 + 同番号を含む
    // type_360 が undefined の場合 (20110 等) は survey_code + 同番号のみ
    const siblings = setNumber
      ? allSets.filter(
          (s) =>
            s.survey_code === clickedSet.survey_code &&
            (s.type_360 || null) === (clickedSet.type_360 || null) &&
            String(s.set_name || '').includes(setNumber)
        )
      : [clickedSet];

    const loadAll = async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          siblings.map((s) =>
            Axios.get(`/api/questionSet/${s._id}/detail`).then((r) => ({
              subType: String(s.sub_type ?? ''),
              data: r.data.success ? r.data : null,
            }))
          )
        );
        const map = {};
        results.forEach(({ subType, data }) => {
          if (data) map[subType] = data;
        });
        setDetailsBySubType(map);
        setActiveTab(String(clickedSet.sub_type ?? ''));
      } catch (error) {
        console.log(error);
        message.error('セット詳細の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [visible, clickedSet, allSets]);

  // モーダル닫힐 때 상태 초기화
  useEffect(() => {
    if (!visible) {
      setDetailsBySubType({});
      setActiveTab(null);
      setSeasonModalSeason(null);
    }
  }, [visible]);

  // -------------------------------------------------------------------
  //  質問をカテゴリで Group 化
  // -------------------------------------------------------------------
  const groupByCategory = (questions) => {
    if (!Array.isArray(questions)) return [];
    const groups = new Map();
    questions.forEach((q) => {
      const cat = q.category_name_upper1 || '(カテゴリ未設定)';
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat).push(q);
    });
    return Array.from(groups.entries());
  };

  // タブのコンテンツ
  const renderQuestionsTab = (detail) => {
    if (!detail) {
      return <Empty description="このサブタイプのセットは登録されていません" />;
    }
    const grouped = groupByCategory(detail.questions);
    if (grouped.length === 0) {
      return <Empty description="質問がありません" />;
    }
    return (
      <Collapse
        defaultActiveKey={grouped.map(([cat]) => cat)}
        items={grouped.map(([cat, items]) => ({
          key: cat,
          label: (
            <Space>
              <b>{cat}</b>
              <Tag color="orange">{items.length} 問</Tag>
            </Space>
          ),
          children: (
            <List
              size="small"
              dataSource={items}
              renderItem={(q) => (
                <List.Item>
                  <Space align="start">
                    <Tag>{q.no}</Tag>
                    <span>{q.question}</span>
                    {q.type === '20' && <Tag color="blue">記述</Tag>}
                  </Space>
                </List.Item>
              )}
            />
          ),
        }))}
      />
    );
  };

  // 使用中シーズンリスト (全 sub_type の和集合)
  const seasons = useMemo(() => {
    const map = new Map();
    Object.values(detailsBySubType).forEach((d) => {
      if (d && Array.isArray(d.seasons)) {
        d.seasons.forEach((s) => map.set(String(s._id), s));
      }
    });
    return Array.from(map.values());
  }, [detailsBySubType]);

  // 現在表示中のセット (クリックされたもの)
  const currentSet = useMemo(() => {
    if (!clickedSet) return null;
    const d = detailsBySubType[String(clickedSet.sub_type ?? '')];
    return d?.questionSet || clickedSet;
  }, [clickedSet, detailsBySubType]);

  // 表示する sub_type 一覧 (expected と実際存在分の和集合)
  const tabSubTypes = useMemo(() => {
    const set = new Set(expectedSubTypes);
    Object.keys(detailsBySubType).forEach((k) => set.add(k));
    if (clickedSet) set.add(String(clickedSet.sub_type ?? ''));
    return Array.from(set).filter(Boolean).sort();
  }, [expectedSubTypes, detailsBySubType, clickedSet]);

  return (
    <>
      <Modal
        open={visible}
        onCancel={onClose}
        title="設問セット詳細"
        width={900}
        footer={[
          <Button key="close" onClick={onClose}>
            閉じる
          </Button>,
          currentSet && (
            <Button
              key="export"
              icon={<DownloadOutlined />}
              onClick={() => onExport && onExport(currentSet)}
            >
              CSV ダウンロード
            </Button>
          ),
          currentSet && (
            <Button
              key="edit"
              type="primary"
              icon={<EditOutlined />}
              onClick={() => onEdit && onEdit(currentSet)}
            >
              編集
            </Button>
          ),
        ]}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
            <Spin />
          </div>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {/* 基本情報 */}
            {currentSet && (
              <Descriptions
                size="small"
                column={2}
                items={[
                  { key: '1', label: 'セット名', children: currentSet.set_name },
                  { key: '2', label: '形式', children: currentSet.type_360 || '-' },
                  {
                    key: '3',
                    label: 'サーベイコード',
                    children: currentSet.survey_code,
                  },
                  {
                    key: '4',
                    label: '区分',
                    children: getSubTypeLabel(currentSet.sub_type),
                  },
                  {
                    key: '5',
                    label: '使用不可',
                    children: currentSet.disabled ? (
                      <Tag color="red">※使用不可</Tag>
                    ) : (
                      <Tag color="green">使用可</Tag>
                    ),
                  },
                  {
                    key: '6',
                    label: '説明',
                    children: currentSet.set_description,
                    span: 2,
                  },
                ]}
              />
            )}

            {/* sub_type 別 동적 タブ */}
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabSubTypes.map((subType) => {
                const detail = detailsBySubType[subType];
                const count = detail?.questions?.length || 0;
                return {
                  key: subType,
                  label: `${getSubTypeLabel(subType)} (${count} 問)`,
                  children: renderQuestionsTab(detail),
                };
              })}
            />

            {/* 使用中シーズン (F-08) */}
            <div>
              <div
                style={{
                  fontSize: 'var(--text-md)',
                  fontWeight: 'var(--font-bold)',
                  marginBottom: 'var(--space-2)',
                }}
              >
                使用中シーズン ({seasons.length})
              </div>
              {seasons.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="使用中のシーズンはありません"
                />
              ) : (
                <List
                  size="small"
                  dataSource={seasons}
                  renderItem={(s) => (
                    <List.Item
                      actions={[
                        <Button
                          key="view"
                          size="small"
                          icon={<LinkOutlined />}
                          onClick={() => setSeasonModalSeason(s)}
                        >
                          詳細
                        </Button>,
                      ]}
                    >
                      <Space>
                        <span>{s.season_name}</span>
                        <Tag>{s.group_code}</Tag>
                        {s.active ? (
                          <Tag color="green">運用中</Tag>
                        ) : (
                          <Tag color="default">停止</Tag>
                        )}
                      </Space>
                    </List.Item>
                  )}
                />
              )}
            </div>
          </Space>
        )}
      </Modal>

      {/* シーズン詳細 sub-modal */}
      <Modal
        open={!!seasonModalSeason}
        onCancel={() => setSeasonModalSeason(null)}
        title="シーズン詳細"
        footer={[
          <Button key="close" onClick={() => setSeasonModalSeason(null)}>
            閉じる
          </Button>,
        ]}
      >
        {seasonModalSeason && (
          <Descriptions
            size="small"
            column={1}
            items={[
              {
                key: '1',
                label: 'シーズン名',
                children: seasonModalSeason.season_name,
              },
              {
                key: '2',
                label: 'グループコード',
                children: seasonModalSeason.group_code,
              },
              {
                key: '3',
                label: 'ステータス',
                children: seasonModalSeason.active ? '運用中' : '停止',
              },
              {
                key: '4',
                label: '開始日',
                children: seasonModalSeason.survey_start_date
                  ? new Date(seasonModalSeason.survey_start_date).toLocaleDateString('ja-JP')
                  : '-',
              },
              {
                key: '5',
                label: '終了日',
                children: seasonModalSeason.survey_end_date
                  ? new Date(seasonModalSeason.survey_end_date).toLocaleDateString('ja-JP')
                  : '-',
              },
            ]}
          />
        )}
      </Modal>
    </>
  );
};

export default QSetDetailModal;
